import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import StorySwarms from "./StorySwarms";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./config/apiConfig";

// Helper function to detect user's location and map to news edition
const detectLocationBasedEdition = () => {
  // Check if user has a saved preference first
  const savedEdition = localStorage.getItem("newsEdition");
  if (savedEdition) {
    return savedEdition;
  }

  try {
    // Method 1: Check browser timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      // UK timezones
      if (
        timezone.includes("London") ||
        timezone.includes("Belfast") ||
        timezone.includes("Edinburgh") ||
        timezone === "Europe/London"
      ) {
        return "uk";
      }
      // US timezones
      if (
        timezone.includes("America/") ||
        timezone.includes("US/") ||
        timezone.includes("New_York") ||
        timezone.includes("Chicago") ||
        timezone.includes("Denver") ||
        timezone.includes("Los_Angeles")
      ) {
        return "us";
      }
    }

    // Method 2: Check browser language/locale
    const language = navigator.language || navigator.userLanguage;
    if (language) {
      const locale = language.toLowerCase();
      // UK English variants
      if (locale === "en-gb" || locale.startsWith("en-gb")) {
        return "uk";
      }
      // US English variants
      if (locale === "en-us" || locale.startsWith("en-us") || locale === "en") {
        return "us";
      }
    }

    // Method 3: Check country from locale if available
    if (Intl.Locale) {
      try {
        const locale = new Intl.Locale(navigator.language);
        if (locale.region) {
          if (locale.region === "GB" || locale.region === "UK") {
            return "uk";
          }
          if (locale.region === "US") {
            return "us";
          }
        }
      } catch (e) {
        // Fallback if Intl.Locale is not supported
      }
    }
  } catch (error) {
    // Silently fail location detection without logging in production
    // console.warn('Could not detect user location:', error);
  }

  // Default to world edition if detection fails
  return "world";
};

const NewsAggregator = ({ user, logout, renderUserInfo }) => {
  const [selectedTopic, setSelectedTopic] = useState("all");
  const [selectedBias, setSelectedBias] = useState("all");
  const [timeFilter, setTimeFilter] = useState("24h");
  const [selectedEdition, setSelectedEdition] = useState(() => {
    return detectLocationBasedEdition();
  });
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rssConfig, setRssConfig] = useState(null);

  // Fetch RSS configuration
  const fetchRssConfig = useCallback(async () => {
    try {
      const response = await apiFetch("/admin/rss-config");

      if (response.ok) {
        const config = await response.json();
        setRssConfig(config);
      }
    } catch (err) {
      // Silently fail - use default config
      setRssConfig({ storySwarmMinArticles: 2 });
    }
  }, []);

  // Fetch stories from backend
  const fetchStories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/news/stories?t=${Date.now()}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setStories(data.stories || []);
      } else {
        throw new Error(data.error || "Failed to fetch stories");
      }
    } catch (err) {
      setError(err.message);
      setStories([]); // Set empty array instead of mock data
    } finally {
      setLoading(false);
    }
  }, []);

  // Load stories and config on component mount
  useEffect(() => {
    fetchStories();
    fetchRssConfig();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchStories, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStories, fetchRssConfig]);

  // Handle edition change and save to localStorage
  const handleEditionChange = (edition) => {
    setSelectedEdition(edition);
    localStorage.setItem("newsEdition", edition);
  };

  // Save the detected edition to localStorage if it's not already saved
  useEffect(() => {
    if (!localStorage.getItem("newsEdition")) {
      localStorage.setItem("newsEdition", selectedEdition);
    }
  }, [selectedEdition]);

  // UI filter data

  const biasFilters = [
    { id: "all", name: "All Perspectives", icon: "⚖️" },
    {
      id: "left",
      name: "Left-Leaning",
      icon: "🔵",
      color: "bg-blue-100 text-blue-800",
    },
    {
      id: "center",
      name: "Center",
      icon: "⚪",
      color: "bg-gray-100 text-gray-800",
    },
    {
      id: "right",
      name: "Right-Leaning",
      icon: "🔴",
      color: "bg-red-100 text-red-800",
    },
  ];

  const timeFilters = [
    { id: "1h", name: "Last Hour" },
    { id: "24h", name: "Last 24 Hours" },
    { id: "7d", name: "Last Week" },
    { id: "30d", name: "Last Month" },
  ];

  // Filter and prioritize stories based on selected filters and edition
  const filteredStories = useMemo(() => {
    // Only show semantically clustered stories
    let filtered = stories.filter(
      (story) => story.clustering_method === "semantic"
    );

    // Filter by topic
    if (selectedTopic !== "all") {
      filtered = filtered.filter(
        (story) =>
          story.topic?.toLowerCase() === selectedTopic.toLowerCase() ||
          story.topic?.toLowerCase().includes(selectedTopic.toLowerCase())
      );
    }

    // Filter by bias perspective
    if (selectedBias !== "all") {
      filtered = filtered.filter((story) => {
        const biasDistribution = story.bias_distribution || story.articleCount;
        if (!biasDistribution) return false;

        const leftCount = biasDistribution.left || 0;
        const centerCount = biasDistribution.center || 0;
        const rightCount = biasDistribution.right || 0;

        switch (selectedBias) {
          case "left":
            return leftCount > centerCount && leftCount > rightCount;
          case "center":
            return centerCount >= leftCount && centerCount >= rightCount;
          case "right":
            return rightCount > leftCount && rightCount > centerCount;
          default:
            return true;
        }
      });
    }

    // Filter by time range
    if (timeFilter !== "24h") {
      const now = new Date();
      const timeThresholds = {
        "1h": 1 * 60 * 60 * 1000,
        "7d": 7 * 24 * 60 * 60 * 1000,
        "30d": 30 * 24 * 60 * 60 * 1000,
      };

      const threshold = timeThresholds[timeFilter];
      if (threshold) {
        filtered = filtered.filter((story) => {
          const storyTime = story.updated_at
            ? new Date(story.updated_at)
            : new Date();
          return now - storyTime <= threshold;
        });
      }
    }

    // Regional prioritization based on edition
    if (selectedEdition !== "world") {
      filtered = filtered
        .map((story) => {
          const regionDistribution = story.region_distribution;
          let priorityScore = 0;

          // If story has regional data, calculate priority normally
          if (regionDistribution) {
            // Calculate priority score based on regional content
            if (selectedEdition === "uk") {
              priorityScore =
                regionDistribution.uk * 3 +
                regionDistribution.international * 1 +
                regionDistribution.us * 0.5;
            } else if (selectedEdition === "us") {
              priorityScore =
                regionDistribution.us * 3 +
                regionDistribution.international * 1 +
                regionDistribution.uk * 0.5;
            }
          } else {
            // Stories without regional data get very low priority (-1000)
            // This pushes them to the bottom regardless of update time
            priorityScore = -1000;
          }

          return { ...story, priorityScore };
        })
        .sort((a, b) => {
          // Sort by priority score first, then by update time
          if (b.priorityScore !== a.priorityScore) {
            return b.priorityScore - a.priorityScore;
          }
          return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
        });
    } else {
      // World edition: sort by update time only
      filtered = filtered.sort(
        (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
      );
    }

    return filtered;
  }, [stories, selectedTopic, selectedBias, timeFilter, selectedEdition]);

  // Update topic counts based on semantic stories only
  const updatedTopics = useMemo(() => {
    const semanticStories = stories.filter(
      (story) => story.clustering_method === "semantic"
    );
    const topicCounts = {
      all: semanticStories.length,
      politics: 0,
      economy: 0,
      international: 0,
      technology: 0,
      healthcare: 0,
      environment: 0,
      general: 0,
    };

    semanticStories.forEach((story) => {
      const topic = story.topic?.toLowerCase() || "general";
      if (topicCounts.hasOwnProperty(topic)) {
        topicCounts[topic]++;
      } else {
        topicCounts.general++;
      }
    });

    return [
      { id: "all", name: "All Topics", count: topicCounts.all },
      { id: "politics", name: "Politics", count: topicCounts.politics },
      { id: "technology", name: "Technology", count: topicCounts.technology },
      { id: "economy", name: "Economy", count: topicCounts.economy },
      {
        id: "international",
        name: "International",
        count: topicCounts.international,
      },
      { id: "healthcare", name: "Healthcare", count: topicCounts.healthcare },
      {
        id: "environment",
        name: "Environment",
        count: topicCounts.environment,
      },
      { id: "general", name: "General", count: topicCounts.general },
    ];
  }, [stories]);

  return (
    <div className="min-h-screen bg-gray-50">
      {renderUserInfo && renderUserInfo()}

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <svg
                  className="h-8 w-8 mr-3 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3v8m0 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v8a2 2 0 002 2h2a2 2 0 002-2zm0 4h.01"
                  />
                </svg>
                News Edition
                <span className="ml-3 text-lg font-medium text-blue-600">
                  {selectedEdition === "uk" && "🇬🇧 UK"}
                  {selectedEdition === "us" && "🇺🇸 US"}
                  {selectedEdition === "world" && "🌍 World"}
                </span>
              </h1>
              <p className="text-gray-600 mt-1">
                Compare how different perspectives cover the same stories
                {selectedEdition !== "world" && (
                  <span className="ml-2 text-sm font-medium text-blue-600">
                    • Prioritizing {selectedEdition.toUpperCase()} sources
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  window.location.href = "/";
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Bias Checker
              </button>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Live Updates
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-64 space-y-6">
            {/* Topic Filter */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Topics
              </h3>
              <div className="space-y-2">
                {updatedTopics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedTopic === topic.id
                        ? "bg-blue-100 text-blue-800"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{topic.name}</span>
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                        {topic.count}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Bias Filter */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Perspective
              </h3>
              <div className="space-y-2">
                {biasFilters.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setSelectedBias(filter.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedBias === filter.id
                        ? filter.color || "bg-blue-100 text-blue-800"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <span className="mr-2">{filter.icon}</span>
                    {filter.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Filter */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Time Range
              </h3>
              <div className="space-y-2">
                {timeFilters.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setTimeFilter(filter.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      timeFilter === filter.id
                        ? "bg-blue-100 text-blue-800"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {filter.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Error Message */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Unable to load latest stories
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                      <p className="mt-1">Showing sample data instead.</p>
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={fetchStories}
                        className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-md hover:bg-red-200"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Regional Edition Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">
                  News Edition
                </h3>
                <span className="text-sm text-gray-500">
                  Choose your regional focus
                </span>
              </div>
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => handleEditionChange("uk")}
                  className={`flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedEdition === "uk"
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <span className="mr-2">🇬🇧</span>
                  UK News
                </button>
                <button
                  onClick={() => handleEditionChange("us")}
                  className={`flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedEdition === "us"
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <span className="mr-2">🇺🇸</span>
                  US News
                </button>
                <button
                  onClick={() => handleEditionChange("world")}
                  className={`flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedEdition === "world"
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <span className="mr-2">🌍</span>
                  World News
                </button>
              </div>
              <div className="mt-3 text-xs text-gray-500">
                {selectedEdition === "uk" &&
                  `Prioritizing UK news sources with international context (${filteredStories.length} stories shown)`}
                {selectedEdition === "us" &&
                  `Prioritizing US news sources with international context (${filteredStories.length} stories shown)`}
                {selectedEdition === "world" &&
                  `Balanced international coverage from all regions (${filteredStories.length} stories shown)`}
              </div>
            </div>

            {/* Story Swarms Section - Now displays all stories */}
            <StorySwarms
              stories={filteredStories}
              minArticles={rssConfig?.storySwarmMinArticles || 2}
            />

            {/* Loading State */}
            {loading && (
              <div className="space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <div className="animate-pulse">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                          <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Stories State */}
            {!loading && !error && stories.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3v8m0 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v8a2 2 0 002 2h2a2 2 0 002-2zm0 4h.01"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  No stories available
                </h3>
                <p className="mt-2 text-gray-500">
                  We're still collecting and analyzing news from various
                  sources. Check back in a few minutes!
                </p>
                <button
                  onClick={fetchStories}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Refresh
                </button>
              </div>
            )}

            {/* No Filtered Stories State */}
            {!loading &&
              !error &&
              stories.length > 0 &&
              filteredStories.length === 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    No stories match your filters
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Try adjusting your topic, perspective, or time range filters
                    to see more stories.
                  </p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {selectedTopic !== "all" && (
                      <button
                        onClick={() => setSelectedTopic("all")}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Clear topic filter
                      </button>
                    )}
                    {selectedBias !== "all" && (
                      <button
                        onClick={() => setSelectedBias("all")}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Clear perspective filter
                      </button>
                    )}
                    {timeFilter !== "24h" && (
                      <button
                        onClick={() => setTimeFilter("24h")}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Clear time filter
                      </button>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

NewsAggregator.propTypes = {
  user: PropTypes.object,
  logout: PropTypes.func,
  renderUserInfo: PropTypes.func,
};

export default NewsAggregator;
